import { useState } from 'react';
import { toast } from 'sonner';
import { FunctionError } from '../../../../lib/edge-functions';
import {
  saveExternalAgent,
  testConnectionDraft,
} from '../../../../lib/external-agents/api';
import type {
  ConnectionDraft,
  ConnectionTestResult,
} from '../../../../lib/external-agents/types';
import {
  type DraftField,
  findDraftErrors,
  findDraftWarnings,
} from '../../../../lib/external-agents/validate';
import {
  ReadyStep,
  type WizardFooter,
  WizardFrame,
  type WizardStep,
} from '../wizard-frame';
import {
  EndpointStep,
  FIELD_IDS,
  FormatStep,
  SaveStep,
  type TestFailure,
  TestStep,
} from './endpoint-steps';

const STEPS: WizardStep[] = [
  { id: 'format', label: 'Format' },
  { id: 'endpoint', label: 'Endpoint' },
  { id: 'test', label: 'Test' },
  { id: 'save', label: 'Save' },
];

type StepId = 'format' | 'endpoint' | 'test' | 'save' | 'done';

const NEW_DRAFT: ConnectionDraft = {
  format: 'json',
  endpointUrl: '',
  authType: 'bearer',
  authHeaderName: 'X-API-Key',
  model: '',
  // Most bots keep their own conversation memory, keyed by a session ID.
  conversationMode: 'session',
  sessionIdSource: 'response',
  sessionResponsePath: 'conversation_id',
  requestTemplate: '',
  replyPath: '',
};

export interface EditedAgent {
  agentId: string;
  name: string;
  greeting: string | null;
  draft: ConnectionDraft;
}

interface EndpointWizardProps {
  workspaceId: string;
  /** Set when editing a saved agent; its secret stays unless replaced. */
  editing?: EditedAgent;
  onClose: () => void;
  /** Called once the agent is saved. */
  onSaved: () => void;
  onRunSimulation: (agentId: string) => void;
}

/** Format → Endpoint → Test → Save, for an agent behind the customer's API. */
export function EndpointWizard({
  workspaceId,
  editing,
  onClose,
  onSaved,
  onRunSimulation,
}: EndpointWizardProps) {
  const [step, setStep] = useState<StepId>(editing ? 'endpoint' : 'format');
  const [draft, setDraft] = useState<ConnectionDraft>(
    editing?.draft ?? NEW_DRAFT,
  );
  const [secret, setSecret] = useState('');
  const [name, setName] = useState(editing?.name ?? '');
  const [greeting, setGreeting] = useState(editing?.greeting ?? '');
  const [test, setTest] = useState<{
    result: ConnectionTestResult;
    forSettings: string;
  } | null>(null);
  const [failure, setFailure] = useState<{
    failure: TestFailure;
    forSettings: string;
  } | null>(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState<{ id: string; name: string } | null>(null);

  // A test proves one exact combination of settings and secret.
  const settingsKey = JSON.stringify({ draft, secret });
  const testResult = test?.forSettings === settingsKey ? test.result : null;
  const passed = testResult?.ok ? testResult : null;

  const testFailure =
    failure?.forSettings === settingsKey ? failure.failure : null;

  const draftErrors = findDraftErrors(draft, secret, Boolean(editing));
  const draftWarnings = findDraftWarnings(draft);

  /** On to the test, or show what to fix first. */
  const continueToTest = () => {
    const first = (Object.keys(FIELD_IDS) as DraftField[]).find(
      (field) => draftErrors[field],
    );
    if (!first) {
      setStep('test');
      return;
    }
    setShowAllErrors(true);
    requestAnimationFrame(() =>
      document.getElementById(FIELD_IDS[first])?.focus(),
    );
  };

  const runTest = async () => {
    setIsTesting(true);
    setFailure(null);
    try {
      const result = await testConnectionDraft(
        workspaceId,
        draft,
        secret,
        editing?.agentId,
      );
      setTest({ result, forSettings: settingsKey });
    } catch (error) {
      setTest(null);
      setFailure({
        failure: {
          message: error instanceof Error ? error.message : 'The test failed.',
          // 422: the server refused the settings themselves.
          fixable: error instanceof FunctionError && error.status === 422,
        },
        forSettings: settingsKey,
      });
    } finally {
      setIsTesting(false);
    }
  };

  const save = async () => {
    if (!passed) return;
    setIsSaving(true);
    try {
      const result = await saveExternalAgent(workspaceId, {
        agentId: editing?.agentId,
        name: name.trim(),
        greeting: greeting.trim(),
        config: draft,
        secret,
        receipt: passed.receipt,
      });
      setSaved({ id: result.agentId, name: result.name });
      onSaved();
      toast.success(`${result.name} saved`);
      setStep('done');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save.');
    } finally {
      setIsSaving(false);
    }
  };

  const footer = ((): WizardFooter => {
    switch (step) {
      case 'format':
        return {
          back: { label: 'Cancel', onClick: onClose },
          next: { label: 'Continue', onClick: () => setStep('endpoint') },
        };
      case 'endpoint':
        return {
          back: editing
            ? { label: 'Cancel', onClick: onClose }
            : { label: 'Back', onClick: () => setStep('format') },
          next: { label: 'Continue', onClick: continueToTest },
        };
      case 'test':
        return {
          back: { label: 'Back', onClick: () => setStep('endpoint') },
          next: {
            label: 'Continue',
            disabled: !passed,
            onClick: () => setStep('save'),
          },
        };
      case 'save':
        return {
          back: { label: 'Back', onClick: () => setStep('test') },
          next: {
            label: isSaving
              ? 'Saving…'
              : editing
                ? 'Save changes'
                : 'Save agent',
            disabled: isSaving || !name.trim() || !passed,
            onClick: save,
          },
        };
      case 'done':
        return {
          back: { label: 'Back to agents', onClick: onClose },
          next: {
            label: 'Run a simulation',
            onClick: () => saved && onRunSimulation(saved.id),
          },
        };
    }
  })();

  return (
    <WizardFrame
      title={editing ? `Edit ${editing.name}` : 'Connect your own API'}
      steps={STEPS}
      current={step}
      onClose={onClose}
      footer={footer}
    >
      {step === 'format' && (
        <FormatStep
          value={draft.format}
          onChange={(format) => setDraft({ ...draft, format })}
        />
      )}
      {step === 'endpoint' && (
        <EndpointStep
          draft={draft}
          onChange={setDraft}
          secret={secret}
          onSecretChange={setSecret}
          hasSavedSecret={Boolean(editing)}
          errors={draftErrors}
          warnings={draftWarnings}
          showAllErrors={showAllErrors}
        />
      )}
      {step === 'test' && (
        <TestStep
          result={testResult}
          failure={testFailure}
          isRunning={isTesting}
          onRun={runTest}
          onEditSettings={() => setStep('endpoint')}
        />
      )}
      {step === 'save' && (
        <SaveStep
          name={name}
          onNameChange={setName}
          greeting={greeting}
          onGreetingChange={setGreeting}
        />
      )}
      {step === 'done' && saved && <ReadyStep name={saved.name} />}
    </WizardFrame>
  );
}
